import { StringDevUIFieldDescriptor } from "../../core/ui/StringDevUISchema";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Abstract base class for all grid adapters.
 *
 * Each adapter is a self-contained unit that:
 * - Declares its own default settings
 * - Describes its UI schema for the settings panel
 * - Renders itself into an SVG overlay
 *
 * To create a new grid type, extend this class and implement
 * all abstract members. The system handles everything else.
 */
export abstract class GridAdapter {
  /** Unique type key used for serialization and registry lookup */
  abstract readonly type: string;

  /** Human-readable label shown in the HUD menu */
  abstract readonly label: string;

  /** SVG icon string (inline SVG markup) */
  abstract readonly icon: string;

  /**
   * Returns the default settings for this adapter.
   * These are used when a new grid instance is created.
   */
  abstract getDefaults(): Record<string, any>;

  /**
   * Returns the UI field descriptors that GridUIBuilder
   * uses to construct the settings panel.
   */
  abstract getUISchema(): StringDevUIFieldDescriptor[];

  /**
   * Renders the grid into the given SVG element.
   *
   * @param svg    The SVG overlay element (same size as target)
   * @param width  Element width in px
   * @param height Element height in px
   * @param settings  Current settings for this instance
   */
  abstract render(
    svg: SVGSVGElement,
    width: number,
    height: number,
    settings: Record<string, any>,
  ): void;

  /**
   * Removes all elements previously rendered by this adapter.
   * Default implementation clears the adapter's group element.
   */
  clear(svg: SVGSVGElement, instanceId: string): void {
    const group = svg.querySelector(`[data-grid-id="${instanceId}"]`);
    if (group) group.remove();
  }

  /**
   * Creates or retrieves a <g> group element scoped to a grid instance.
   * All rendering should happen inside this group for clean cleanup.
   */
  protected getGroup(svg: SVGSVGElement, instanceId: string): SVGGElement {
    let group = svg.querySelector(
      `[data-grid-id="${instanceId}"]`,
    ) as SVGGElement | null;
    if (!group) {
      group = document.createElementNS(SVG_NS, "g") as SVGGElement;
      group.setAttribute("data-grid-id", instanceId);
      svg.appendChild(group);
    }
    return group;
  }

  /**
   * Helper: creates an SVG line element.
   */
  protected createLine(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: string,
    opacity: number,
    strokeWidth: number = 1,
  ): SVGLineElement {
    const line = document.createElementNS(SVG_NS, "line") as SVGLineElement;
    line.setAttribute("data-string-grid-shape", "line");
    line.setAttribute("x1", String(x1));
    line.setAttribute("y1", String(y1));
    line.setAttribute("x2", String(x2));
    line.setAttribute("y2", String(y2));
    line.setAttribute("stroke", color);
    line.setAttribute("stroke-opacity", String(opacity));
    line.setAttribute("stroke-width", String(strokeWidth));
    return line;
  }

  /**
   * Helper: creates an SVG rect element.
   */
  protected createRect(
    x: number,
    y: number,
    width: number,
    height: number,
    fill: string,
    opacity: number,
  ): SVGRectElement {
    const rect = document.createElementNS(SVG_NS, "rect") as SVGRectElement;
    rect.setAttribute("data-string-grid-shape", "rect");
    rect.setAttribute("x", String(x));
    rect.setAttribute("y", String(y));
    rect.setAttribute("width", String(width));
    rect.setAttribute("height", String(height));
    rect.setAttribute("fill", fill);
    rect.setAttribute("fill-opacity", String(opacity));
    return rect;
  }

  /**
   * Converts a value from the given unit to pixels.
   *
   * @param value     Raw numeric value
   * @param unit      "px" | "%" | "vw" | "vh" | "em" | "rem"
   * @param dimension Reference dimension (element width or height) for "%" mode
   */
  protected resolveUnit(
    value: number,
    unit: string,
    dimension: number,
    referenceElement?: Element | null,
  ): number {
    const rootFontSize =
      parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const elementFontSize =
      referenceElement instanceof Element
        ? parseFloat(getComputedStyle(referenceElement).fontSize) || rootFontSize
        : rootFontSize;

    switch (unit) {
      case "%":  return (value / 100) * dimension;
      case "vw": return (value / 100) * window.innerWidth;
      case "vh": return (value / 100) * window.innerHeight;
      case "em": return value * elementFontSize;
      case "rem": return value * rootFontSize;
      default:   return value;
    }
  }

  /**
   * Helper: creates an SVG path element.
   */
  protected createPath(
    d: string,
    stroke: string,
    opacity: number,
    strokeWidth: number = 1,
    fill: string = "none",
  ): SVGPathElement {
    const path = document.createElementNS(SVG_NS, "path") as SVGPathElement;
    path.setAttribute("data-string-grid-shape", "path");
    path.setAttribute("d", d);
    path.setAttribute("stroke", stroke);
    path.setAttribute("stroke-opacity", String(opacity));
    path.setAttribute("stroke-width", String(strokeWidth));
    path.setAttribute("fill", fill);
    return path;
  }
}
